import { ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight, CalendarDays } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { DateTime, Settings } from 'luxon';
import { CSSTransition } from "react-transition-group";
import { useClickOutside } from '@/utils';
import './Datepicker.scss';

type Day = {
    day: number,
    originalDate: DateTime,
    isInCurrentMonth: boolean,
}
type Month = {
    name: string,
    index: number
}

interface DatepickerProps {
    type: "dateIssuedSelector" | "dateDueSelector";
    isError: boolean;
    name: string;
    dateIssued: DateTime,
    dateDue: DateTime,
    setValue: (value: DateTime) => void
}

Settings.defaultLocale = "en";

export const Datepicker = ({ type, name, isError, dateDue, dateIssued, setValue }: DatepickerProps) => {
    const date = type === "dateDueSelector" ? dateDue : dateIssued;
    const [currentDate, setCurrentDate] = useState<DateTime>(date);
    const [selectedDate, setSelectedDate] = useState<DateTime>(currentDate);
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const [view, setView] = useState<"day" | "month" | "year">("day");
    const areaRef = useRef<HTMLDivElement>(null);

    const getDaysInMonth = () => {
        const start = currentDate.startOf("month").startOf("week", { useLocaleWeeks: true });
        const days: Day[] = [];
        let current = start;
        while (days.length < 42) {
            days.push({
                day: current.day,
                originalDate: current,
                isInCurrentMonth: current.month === currentDate.month,
            });
            current = current.plus({ days: 1 });
        }
        return days;
    };

    const changeMonth = (increment: number) => {
        const newDate = currentDate.plus({ months: increment });
        setCurrentDate(newDate);
    };

    const changeYear = (increment: number) => {
        const newDate = currentDate.plus({ years: increment });
        setCurrentDate(newDate);
    };

    const addDaysToDate = (days: number) => {
        if (date) {
            const newDate = date.plus({ days });
            setValue(newDate);
            setCurrentDate(newDate);
        }
    };
    const getYearsList = (year: number): number[] => {
        let start = year - 5;
        if (start < 0) start = 0;
        const end = year + 4;
        return Array.from({ length: end - start + 1 }, (_, index) => start + index);
    }
    const [years, setYears] = useState<number[]>(getYearsList(currentDate.year));
    const customDayNames: string[] = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    const months: Month[] = [
        { name: "Jan", index: 1 },
        { name: "Feb", index: 2 },
        { name: "Mar", index: 3 },
        { name: "Apr", index: 4 },
        { name: "May", index: 5 },
        { name: "Jun", index: 6 },
        { name: "Jul", index: 7 },
        { name: "Aug", index: 8 },
        { name: "Sep", index: 9 },
        { name: "Oct", index: 10 },
        { name: "Nov", index: 11 },
        { name: "Dec", index: 12 }];

    useClickOutside(areaRef, () => {
        setIsVisible(false);
    });
    useEffect(() => {
        if (type === "dateDueSelector") setCurrentDate(dateDue);
        else setCurrentDate(dateIssued);
    }, [dateIssued, dateDue, type]);

    return (
        <div ref={areaRef}>
            <button
                onClick={() => {
                    setIsVisible(!isVisible);
                }}
                className={`open ${isError ? "open-error" : ""}`}
                type="button">
                <CalendarDays width={14} height={14} />{name} {date.toFormat('dd/MM/yyyy')}
            </button>
            <CSSTransition in={isVisible}
                timeout={200}
                classNames="fade"
                exit={false}
                id={type}
                unmountOnExit>
                <div className="datepicker-container">
                    <div className="datepicker">
                        {type === "dateDueSelector" &&
                            <div className="datepicker__controls">
                                <p>Payment Terms</p>
                                <button type="button" onClick={() => addDaysToDate(15)}>15 Days</button>
                                <button type="button" onClick={() => addDaysToDate(30)}>30 Days</button>
                                <button type="button" onClick={() => addDaysToDate(60)}>60 Days</button>
                                <button type="button" onClick={() => addDaysToDate(90)}>90 Days</button>
                            </div>
                        }
                        <div className="datepicker__calendar">
                            <div className="datepicker__header">
                                <button type="button" className="datepicker__header-button" onClick={() => {
                                    if (view === "year") setYears(getYearsList(years[0] - 6))
                                    else changeYear(-1)
                                }}>
                                    <ChevronsLeft />
                                </button>
                                {view === "day" &&
                                    <button type="button" className="datepicker__header-button" onClick={() => changeMonth(-1)}>
                                        <ChevronLeft />
                                    </button>}
                                <span className="datepicker__current-date">
                                    {view === "day" && <button type="button" onClick={() => {
                                        setView("month");
                                    }}>{currentDate.toFormat("MMMM")}</button>}
                                    {view !== "year" && <button type="button" onClick={() => {
                                        setView("year")
                                    }}>{currentDate.year}</button>}
                                    {view === "year" && <div>
                                        {years[0]}
                                        -
                                        {years[years.length - 1]}
                                    </div>}
                                </span>
                                {view === "day" &&
                                    <button type="button" className="datepicker__header-button" onClick={() => changeMonth(1)}>
                                        <ChevronRight />
                                    </button>}
                                <button type="button" className="datepicker__header-button" onClick={() => {
                                    if (view === "year") setYears(getYearsList(years[years.length - 1] + 6))
                                    else changeYear(1)
                                }}>
                                    <ChevronsRight />
                                </button>
                            </div>

                            {view === "day" &&
                                <div className="datepicker__week-days">
                                    {customDayNames.map((day, index) => (
                                        <div key={index} className="datepicker__week-day">{day}</div>
                                    ))}
                                </div>}
                            <div className="datepicker__view-container">
                                {view === "day" && <div className="datepicker__days">
                                    {getDaysInMonth().map(({ day, originalDate, isInCurrentMonth }, index) => (

                                        <button
                                            type="button"
                                            key={index}
                                            className={`datepicker__day ${(selectedDate.day === originalDate.day
                                                && selectedDate.month === originalDate.month && selectedDate.year === originalDate.year) ? 'selected' : ''} 
                                            ${isInCurrentMonth === false ? "not-current" : ""}`}
                                            onClick={() => {
                                                setValue(originalDate);
                                                setSelectedDate(originalDate);
                                                setIsVisible(false);
                                            }}
                                        >
                                            {day}
                                        </button>
                                    ))}
                                </div>}
                                {view === "month" && <div className="datepicker__months">
                                    {months.map(({ name, index }) => (
                                        <button
                                            type="button"
                                            className={`datepicker__month ${(currentDate.month === index) ? 'selected' : ''}`}
                                            onClick={() => {
                                                const updated = currentDate.set({ month: index, year: currentDate.year })
                                                setCurrentDate(updated);
                                                setView("day");
                                            }}
                                            key={index}>
                                            {name}
                                        </button>))}
                                </div>}
                                {view === "year" && <div className="datepicker__years">
                                    {years.map((year) => (
                                        <button
                                            type="button"
                                            className={`datepicker__month ${(currentDate.year === year) ? 'selected' : ''}`}
                                            onClick={() => {
                                                const updated = currentDate.set({ year });
                                                setCurrentDate(updated);
                                                setView("month");
                                            }}>
                                            {year}
                                        </button>
                                    ))}
                                </div>}
                            </div>
                        </div>
                    </div>
                </div>
            </CSSTransition>
        </div>
    );
};

